import { DateTime } from "luxon";

import { isoDate } from "@core/foundation/date";

import type { LogEntry, LogEntryCell } from "@apis/calorie-grid/models";

import { shapeManager } from "../../../shared/lib/ShapeManager.ts";

interface PlacementCell {
  readonly col: number;
  readonly day: number;
}

interface PlanDropParams {
  readonly calories: number;
  readonly columns: number;
  readonly logEntries: readonly LogEntry[];
  readonly squareKcal: number;
  readonly squaresPerRow: number;
  readonly today: Date;
}

interface DropParams {
  readonly columns: number;
  readonly occupied: readonly PlacementCell[];
  readonly rows: number;
  readonly squares: number;
  readonly squaresPerRow: number;
  readonly startDay: number;
}

interface DropResult {
  readonly cells: readonly PlacementCell[];
  readonly placed: boolean;
  readonly raised: boolean;
}

class PlacementManager {
  public planDrop({
    calories,
    columns,
    logEntries: allLogEntries,
    squareKcal,
    squaresPerRow,
    today,
  }: PlanDropParams): readonly LogEntryCell[] {
    const logEntries = allLogEntries.filter(item => item.status === "dropped");

    const startOfToday = DateTime.fromJSDate(today).startOf("day");

    const caloriesBefore = logEntries.reduce(
      (sum, logEntry) => sum + logEntry.totalCalories,
      0,
    );

    const squares = shapeManager.marginalSquareCount(
      caloriesBefore,
      calories,
      squareKcal,
    );

    const occupied = logEntries
      .flatMap(logEntry =>
        (logEntry.cells ?? []).map(cell => {
          const { days } = DateTime.fromISO(cell.date)
            .startOf("day")
            .diff(startOfToday, "days");

          return { col: cell.col, day: Math.round(days) };
        }),
      )
      .filter(cell => cell.day >= 0);

    const maxOccupiedDay = occupied.reduce(
      (max, cell) => Math.max(max, cell.day),
      0,
    );

    const result = this.computeDrop({
      columns,
      occupied,
      rows: maxOccupiedDay + squares + 1,
      squares,
      squaresPerRow,
      startDay: 0,
    });

    if (!result.placed) {
      throw new Error("No space to place this purchase yet.");
    }

    return result.cells.map(cell => {
      return {
        col: cell.col,
        date: isoDate(startOfToday.plus({ days: cell.day })),
      };
    });
  }

  private computeDrop({
    columns,
    occupied,
    rows,
    squares,
    squaresPerRow,
    startDay,
  }: DropParams): DropResult {
    if (squares <= 0) {
      return { cells: [], placed: true, raised: false };
    }

    const width = clamp(squaresPerRow, 1, columns);

    const rowCount = Math.ceil(squares / width);

    if (rowCount > rows) {
      return { cells: [], placed: false, raised: false };
    }

    const lastRowWidth = squares - width * (rowCount - 1);

    const rightmost = new Map<number, number>();

    for (const cell of occupied) {
      const current = rightmost.get(cell.day) ?? 0;
      rightmost.set(cell.day, Math.max(current, cell.col + 1));
    }

    const maxStart = rows - rowCount;
    const requested = clamp(startDay, 0, maxStart);
    const fit = findFit({
      columns,
      maxStart,
      requested,
      rightmost,
      rowCount,
      width,
    });

    if (!fit) {
      return { cells: [], placed: false, raised: false };
    }

    const cells = Array.from({ length: rowCount }, (_row, r) => {
      const rowWidth = r === rowCount - 1 ? lastRowWidth : width;

      return Array.from({ length: rowWidth }, (_col, c) => {
        return { col: fit.anchor + c, day: fit.day + r };
      });
    }).flat();

    return { cells, placed: true, raised: fit.day > startDay };
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function findFit({
  columns,
  maxStart,
  requested,
  rightmost,
  rowCount,
  width,
}: {
  readonly columns: number;
  readonly maxStart: number;
  readonly requested: number;
  readonly rightmost: ReadonlyMap<number, number>;
  readonly rowCount: number;
  readonly width: number;
}): { readonly anchor: number; readonly day: number } | null {
  for (let day = requested; day <= maxStart; day++) {
    const anchor = Math.max(
      ...Array.from(
        { length: rowCount },
        (_value, r) => rightmost.get(day + r) ?? 0,
      ),
    );

    if (anchor + width <= columns) {
      return { anchor, day };
    }
  }

  return null;
}

export const placementManager = new PlacementManager();
